import { filterDepartmentJobTitles } from './department_job_title_filter';

const GROUP_SELECTOR = '.devise-form-wrapper__picker-group';
const GROUP_TOGGLE_SELECTOR = '.devise-form-wrapper__picker-group-toggle';
const OPTION_SELECTOR = '.devise-form-wrapper__picker-option';
const NAME_SELECTOR = '.devise-form-wrapper__picker-name';
const DESCRIPTION_SELECTOR = '.devise-form-wrapper__picker-description';
const ACTIVE_CLASS = 'devise-form-wrapper__picker-item_active';

const LIST_MAX_HEIGHT = 360;
const LIST_GAP = 4;
const VIEWPORT_MARGIN = 8;

const searchableText = (element) => [
  element.querySelector(NAME_SELECTOR)?.textContent,
  element.querySelector(DESCRIPTION_SELECTOR)?.textContent,
].join(' ');

const setGroupExpanded = (group, expanded) => {
  group.querySelector(GROUP_TOGGLE_SELECTOR)
    .setAttribute('aria-expanded', String(expanded));
  const options = group.querySelector('.devise-form-wrapper__picker-options');
  options.hidden = !expanded;
};

const nameOf = (element) => element.querySelector(NAME_SELECTOR).textContent.trim();

// "Finance · Manager": the title alone is ambiguous, since several departments
// share it. The flat Other option has no group, so it shows its own name only.
const selectionLabel = (option) => {
  const group = option.closest(GROUP_SELECTOR);

  return group
    ? `${nameOf(group.querySelector(GROUP_TOGGLE_SELECTOR))} · ${nameOf(option)}`
    : nameOf(option);
};

/**
 * Grouped department/job-title combobox (a tree popup) on the sign-up profile step
 * (`#department-job-title-picker`, rendered only behind the
 * cup-signup-department-job-title-picker flag). The search box filters
 * departments and job titles on name and description; picking a job title
 * fills the hidden department + job_title pair the form submits. The box stays
 * invalid while it holds text that is not a picked option, which keeps the
 * required-fields gate from enabling the submit. No-ops when the picker is
 * absent.
 */
export const initDepartmentJobTitlePicker = () => {
  const picker = document.getElementById('department-job-title-picker');
  if (!picker) return;

  const search = picker.querySelector('#work_description_search');
  const list = picker.querySelector('#work_description_options');
  const departmentInput = picker.querySelector('#department');
  const jobTitleInput = picker.querySelector('#job_title');
  const groups = [...list.querySelectorAll(GROUP_SELECTOR)];
  const groupOptions = groups.map((group) => [...group.querySelectorAll(OPTION_SELECTOR)]);
  const flatOptions = [...list.querySelectorAll(`:scope > ${OPTION_SELECTOR}`)];
  const emptyState = list.querySelector('.devise-form-wrapper__picker-empty');
  const catalog = {
    groups: groups.map((group, index) => ({
      text: searchableText(group.querySelector(GROUP_TOGGLE_SELECTOR)),
      options: groupOptions[index].map(searchableText),
    })),
    flatOptions: flatOptions.map(searchableText),
  };
  // The highlighted department header or job title; the combobox keeps focus
  // and points at it through aria-activedescendant.
  let activeItem = null;
  let query = '';

  const isOpen = () => !list.hidden;

  const setActive = (item) => {
    activeItem?.classList.remove(ACTIVE_CLASS);
    activeItem = item;
    if (item) {
      item.classList.add(ACTIVE_CLASS);
      search.setAttribute('aria-activedescendant', item.id);
      item.scrollIntoView({ block: 'nearest' });
    } else {
      search.removeAttribute('aria-activedescendant');
    }
  };

  // The list is position: fixed so the scrolling profile step cannot clip it;
  // it opens above the box when the viewport has more room there.
  const positionList = () => {
    const box = search.getBoundingClientRect();
    const spaceBelow = window.innerHeight - box.bottom - LIST_GAP - VIEWPORT_MARGIN;
    const spaceAbove = box.top - LIST_GAP - VIEWPORT_MARGIN;
    const opensUp = spaceBelow < Math.min(LIST_MAX_HEIGHT, spaceAbove);
    const maxHeight = Math.min(LIST_MAX_HEIGHT, opensUp ? spaceAbove : spaceBelow);

    Object.assign(list.style, {
      left: `${box.left}px`,
      width: `${box.width}px`,
      maxHeight: `${maxHeight}px`,
      top: opensUp ? '' : `${box.bottom + LIST_GAP}px`,
      bottom: opensUp ? `${window.innerHeight - box.top + LIST_GAP}px` : '',
    });
  };

  const open = () => {
    list.hidden = false;
    search.setAttribute('aria-expanded', 'true');
    positionList();
  };

  const close = () => {
    list.hidden = true;
    search.setAttribute('aria-expanded', 'false');
    setActive(null);
  };

  // Capture phase: the scroll comes from the profile step, not the window.
  // Once the box scrolls out of that step's view, a floating list would point
  // at nothing, so it closes instead of following.
  const scrollContainer = search.closest('#profile-step') ?? document.documentElement;
  const repositionWhileOpen = () => {
    if (!isOpen()) return;

    const box = search.getBoundingClientRect();
    const view = scrollContainer.getBoundingClientRect();
    if (box.bottom < view.top || box.top > view.bottom) {
      close();
      return;
    }
    positionList();
  };
  window.addEventListener('scroll', repositionWhileOpen, { capture: true, passive: true });
  window.addEventListener('resize', repositionWhileOpen);

  const applyFilter = (rawQuery) => {
    query = rawQuery.trim();
    const visibility = filterDepartmentJobTitles(catalog, rawQuery);

    for (const [index, { visible, expanded, options }] of visibility.groups.entries()) {
      groups[index].hidden = !visible;
      if (expanded !== null) setGroupExpanded(groups[index], expanded);
      for (const [optionIndex, option] of groupOptions[index].entries()) {
        option.hidden = !options[optionIndex];
      }
    }
    for (const [index, visible] of visibility.flatOptions.entries()) {
      flatOptions[index].hidden = !visible;
    }
    emptyState.hidden = !visibility.empty;
  };

  // Browsing (empty search) steps through the department headers too, since
  // every group starts collapsed and a keyboard-only visitor could otherwise
  // reach nothing but Other. A search already opens the matching groups, so
  // the arrows go straight through the matching job titles.
  const navigableItems = () => {
    const browsing = query === '';
    const selector = browsing ? `${GROUP_TOGGLE_SELECTOR}, ${OPTION_SELECTOR}` : OPTION_SELECTOR;

    return [...list.querySelectorAll(selector)].filter((item) => !item.closest('[hidden]'));
  };

  // With nothing highlighted yet, ArrowDown starts at the first item and
  // ArrowUp at the last, rather than stepping from a -1 index.
  const moveActive = (step) => {
    const items = navigableItems();
    if (items.length === 0) return;

    const current = items.indexOf(activeItem);
    const firstFromNothing = step > 0 ? 0 : items.length - 1;
    const next = current === -1 ? firstFromNothing : current + step;
    setActive(items[(next + items.length) % items.length]);
  };

  const toggleGroup = (toggle) => {
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    setGroupExpanded(toggle.closest(GROUP_SELECTOR), !expanded);
  };

  const clearSelected = () => {
    for (const selected of list.querySelectorAll('[aria-selected="true"]')) {
      selected.setAttribute('aria-selected', 'false');
    }
  };

  const select = (option) => {
    clearSelected();
    option.setAttribute('aria-selected', 'true');
    departmentInput.value = option.dataset.department;
    jobTitleInput.value = option.dataset.jobTitle;
    search.value = selectionLabel(option);
    search.setCustomValidity('');
    jobTitleInput.dispatchEvent(new Event('change', { bubbles: true }));
    close();
    applyFilter('');
  };

  // Typing discards any earlier pick: the box now holds a query, not a choice.
  search.addEventListener('input', () => {
    departmentInput.value = '';
    jobTitleInput.value = '';
    clearSelected();
    search.setCustomValidity(search.value.trim() === '' ? '' : search.dataset.invalidChoiceMessage);
    applyFilter(search.value);
    open();
    setActive(null);
  });

  search.addEventListener('focus', open);
  search.addEventListener('click', open);

  search.addEventListener('keydown', (event) => {
    const handlers = {
      ArrowDown: () => {
        open();
        moveActive(1);
      },
      ArrowUp: () => {
        open();
        moveActive(-1);
      },
      Enter: () => {
        if (!isOpen() || !activeItem) return;

        if (activeItem.matches(GROUP_TOGGLE_SELECTOR)) {
          toggleGroup(activeItem);
        } else {
          select(activeItem);
        }
      },
      Escape: () => close(),
    };
    const handler = handlers[event.key];
    if (!handler) return;

    // Enter on the open list picks an option rather than submitting the form.
    if (event.key !== 'Enter' || isOpen()) event.preventDefault();
    handler();
  });

  // Keeps focus in the search box while the list is clicked, so a click does
  // not blur-close the list before it lands.
  list.addEventListener('mousedown', (event) => event.preventDefault());

  list.addEventListener('click', (event) => {
    const toggle = event.target.closest(GROUP_TOGGLE_SELECTOR);
    if (toggle) {
      toggleGroup(toggle);
      return;
    }

    const option = event.target.closest(OPTION_SELECTOR);
    if (option) select(option);
  });

  search.addEventListener('blur', close);
};
